// frontend/src/admin/v2/components/StatusPill.jsx
import { Box, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { motion } from 'framer-motion'

/**
 * StatusPill — single-line health indicator for the AdminLayout header.
 *
 * @description
 *   A coloured dot + caption text reflecting the global admin status.
 *   The dot animates on each status change (200ms ease-out scale-in).
 *
 * @useWhen
 *   - Inside AdminLayout header to show the system-wide health glance
 *
 * @dontUseFor
 *   - Per-item status (use a Chip with the same colour palette)
 *
 * @props
 *   status: 'green' | 'amber' | 'red' | 'loading' — drives colour + text
 *   compact: boolean — drops the VISIBLE caption, keeps only the dot (mobile).
 *     The caption stays for screen readers (visually hidden) and as a `title`
 *     tooltip — the dot alone would carry the status by colour only (Rule 22 §2).
 */
const COLOR_MAP = {
  green:   { dot: 'success.main',  text: 'All systems operational' },
  amber:   { dot: 'warning.main',  text: '1 or more issues' },
  red:     { dot: 'error.main',    text: 'Service degraded' },
  loading: { dot: 'text.disabled', text: 'Checking…' },
}

export default function StatusPill({ status = 'loading', compact = false }) {
  const cfg = COLOR_MAP[status] || COLOR_MAP.loading
  return (
    <Box
      title={compact ? cfg.text : undefined}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
    >
      <motion.div
        key={status}
        initial={{ scale: 0.85, opacity: 0.6 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{ display: 'inline-flex' }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.dot }} />
      </motion.div>
      {compact ? (
        <Box component="span" sx={visuallyHidden}>{cfg.text}</Box>
      ) : (
        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {cfg.text}
        </Typography>
      )}
    </Box>
  )
}
